import { ProductTypes } from 'modules/products'
import { convertCentsToAmount, formatCurrency } from 'shared/utils/currency'

import { AddItemTypes, Cart, CartItem } from '../types'

export const createOptimisticCartItem = (
  item: AddItemTypes,
  cart: Cart,
  product: ProductTypes
): CartItem => {
  const hasSpecialOffer =
    product.specialOfferPricing &&
    product.pricing &&
    product.specialOfferPricing.priceGross &&
    product.specialOfferPricing.priceGross.value !== null &&
    product.specialOfferPricing.priceGross.value <
      product.pricing?.priceGross?.value

  const priceNetValue = convertCentsToAmount(
    product.pricing?.priceNet?.value || 0
  )
  const priceGrossValue = convertCentsToAmount(
    product.pricing?.priceGross?.value || 0
  )
  const specialPriceNetValue = convertCentsToAmount(
    product.specialOfferPricing?.priceNet?.value || 0
  )
  const specialPriceGrossValue = convertCentsToAmount(
    product.specialOfferPricing?.priceGross?.value || 0
  )

  return {
    id: `temp-${Date.now()}`,
    code: Number(product.code),
    cartId: cart.id,
    productId: item.productId,
    quantity: item.quantity,
    orderedQuantity: 0,
    priceNet: product.pricing?.priceNet
      ? {
          ...product.pricing.priceNet,
          formatted: formatCurrency(
            priceNetValue,
            product.pricing.priceNet.symbol
          )
        }
      : {
          value: 0,
          formatted: '---',
          symbol: '---'
        },
    vatRate: typeof product.vatRate === 'number' ? product.vatRate : 0,
    priceGross: product.pricing?.priceGross
      ? {
          ...product.pricing.priceGross,
          formatted: formatCurrency(
            priceGrossValue,
            product.pricing.priceGross.symbol
          )
        }
      : {
          value: 0,
          formatted: '---',
          symbol: '---'
        },
    promotionId: null,
    specialOfferPriceGross: hasSpecialOffer
      ? {
          ...product.specialOfferPricing?.priceGross,
          formatted: formatCurrency(
            specialPriceGrossValue,
            product.specialOfferPricing?.priceGross?.symbol
          )
        }
      : null,
    specialOfferValueGross: hasSpecialOffer
      ? {
          ...product.specialOfferPricing?.priceGross,
          formatted: formatCurrency(
            specialPriceGrossValue * item.quantity,
            product.specialOfferPricing?.priceGross?.symbol
          )
        }
      : null,
    specialOfferPriceNet: hasSpecialOffer
      ? {
          ...product.specialOfferPricing?.priceNet,
          formatted: formatCurrency(
            specialPriceNetValue,
            product.specialOfferPricing?.priceNet?.symbol
          )
        }
      : null,
    specialOfferValueNet: hasSpecialOffer
      ? {
          ...product.specialOfferPricing?.priceNet,
          formatted: formatCurrency(
            specialPriceNetValue * item.quantity,
            product.specialOfferPricing?.priceNet?.symbol
          )
        }
      : null,
    valueNet: product.pricing?.priceNet
      ? {
          ...product.pricing.priceNet,
          value: product.pricing.priceNet.value * item.quantity,
          formatted: formatCurrency(
            priceNetValue * item.quantity,
            product.pricing.priceNet.symbol
          )
        }
      : {
          value: 0,
          formatted: '---',
          symbol: '---'
        },
    valueVat: product.pricing?.priceNet
      ? {
          ...product.pricing.priceNet,
          value:
            (product.pricing.priceGross.value -
              product.pricing.priceNet.value) *
            item.quantity,
          formatted: formatCurrency(
            (priceGrossValue - priceNetValue) * item.quantity,
            product.pricing.priceNet.symbol
          )
        }
      : {
          value: 0,
          formatted: '---',
          symbol: '---'
        },
    valueGross: product.pricing?.priceGross
      ? {
          ...product.pricing.priceGross,
          value: product.pricing.priceGross.value * item.quantity,
          formatted: formatCurrency(
            priceGrossValue * item.quantity,
            product.pricing.priceGross.symbol
          )
        }
      : {
          value: 0,
          formatted: '---',
          symbol: '---'
        },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    product: product,
    isAvailable: true,
    isAvailableForDeliverySlot: true
  }
}

export const handleOptimisticAdd = (
  currentCart: Cart | null,
  items: AddItemTypes[]
): Cart | null => {
  if (!currentCart) return currentCart

  const newCart = JSON.parse(JSON.stringify(currentCart)) as Cart

  if (!Array.isArray(newCart.items) || newCart.items.length === 0) {
    newCart.items = [] as CartItem[]
  }

  items.forEach(item => {
    const existingItemIndex = newCart.items.findIndex(
      (i: CartItem) => i.product.id === item.productId
    )

    if (existingItemIndex >= 0) {
      newCart.items[existingItemIndex].quantity = item.quantity
    } else {
      const productItem = currentCart.items.find(
        i => i.product.id === item.productId
      )

      const product = productItem?.product ?? item.product

      if (product) {
        const cartItem = createOptimisticCartItem(item, newCart, product)

        newCart.items = [...(newCart.items as CartItem[]), cartItem]
      } else {
        console.warn(
          'Cannot create optimistic cart item: missing product data',
          item
        )
      }
    }
  })

  return newCart
}

export const handleOptimisticUpdate = (
  currentCart: Cart | null,
  itemId: string,
  quantity: number
): Cart | null => {
  if (!currentCart) return currentCart

  const newCart = JSON.parse(JSON.stringify(currentCart)) as Cart

  if (!Array.isArray(newCart.items) || newCart.items.length === 0) {
    newCart.items = [] as CartItem[]

    return newCart
  }

  const itemIndex = newCart.items.findIndex(i => i.id === itemId)

  if (itemIndex >= 0) {
    newCart.items[itemIndex].quantity = quantity
  }

  return newCart
}

export const handleOptimisticRemove = (
  currentCart: Cart | null,
  itemId: string
): Cart | null => {
  if (!currentCart) return currentCart

  const newCart = JSON.parse(JSON.stringify(currentCart)) as Cart

  if (!Array.isArray(newCart.items) || newCart.items.length === 0) {
    newCart.items = [] as CartItem[]

    return newCart
  }

  const itemToRemove = newCart.items.find(i => i.id === itemId)

  if (itemToRemove) {
    newCart.items = newCart.items.filter(i => i.id !== itemId)
  }

  return newCart
}
